import React from 'react';
import { Chip, Stack, Typography, alpha, useTheme } from '@mui/material';
import { layoutGap } from '../../../theme/theme';

export interface WorkspaceFooterProps {
  statusLabel: string;
  detailText: string;
  requiredOpen: number;
  compact?: boolean;
}

/**
 * Compact footer for workspace-style pages (MappingConfiguration, etc.)
 * Uses smaller font sizes appropriate for dense workspace UIs
 */
export const WorkspaceFooter: React.FC<WorkspaceFooterProps> = ({
  statusLabel,
  detailText,
  requiredOpen,
  compact = true,
}) => {
  const theme = useTheme();

  if (!compact) {
    return (
      <Stack direction="row" spacing={layoutGap.dense} alignItems="center">
        <Chip
          size="small"
          label={statusLabel}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor:
              requiredOpen > 0
                ? alpha(theme.palette.warning.main, 0.12)
                : alpha(theme.palette.success.main, 0.12),
            color:
              requiredOpen > 0
                ? theme.palette.warning.main
                : theme.palette.success.main,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
          }}
        >
          {detailText}
        </Typography>
      </Stack>
    );
  }

  // Compact variant for dense workspace UI
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: layoutGap.micro, sm: layoutGap.cozy }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      useFlexGap
      sx={{ width: '100%', minWidth: 0 }}
    >
      <Chip
        size="small"
        label={statusLabel}
        sx={{
          height: 16,
          fontSize: '0.54rem',
          fontWeight: 700,
          backgroundColor:
            requiredOpen > 0
              ? alpha(theme.palette.warning.main, 0.16)
              : alpha(theme.palette.success.main, 0.14),
          color:
            requiredOpen > 0
              ? theme.palette.warning.light
              : theme.palette.success.light,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: alpha(theme.palette.text.secondary, 0.6),
          fontSize: '0.52rem',
          whiteSpace: { xs: 'normal', sm: 'nowrap' },
          textWrap: 'pretty',
          lineHeight: 1.3,
          maxWidth: '100%',
        }}
      >
        {detailText}
      </Typography>
    </Stack>
  );
};

export default WorkspaceFooter;
